import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
    Autocomplete,
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {isSpecialBin, parseBinName, SaveInventoryItem, SaveInventoryRequestItem} from '../../utils'
import {CachedProduct, getCachedProducts} from '../../productCache'
import SaveIcon from '@mui/icons-material/Save'
import excludedSound from '../../../assets/sounds/excluded.mp3'
import {ExcludedDetailsDialog, StatusTooltip} from '../../../common/ui'

interface NonSerialsEditProps {
    nonSerialItems: SaveInventoryItem[]
    selectedSection: string | null
    onSelectSection: (section: string) => void
    saveRequestItem: (requestItem: SaveInventoryRequestItem) => void
    currentBin: string | null
}

interface QueuedEdit {
    sku: string
    previousQty: number
    currentQty: number
}

function NonSerialsEdit({
                            nonSerialItems,
                            selectedSection,
                            onSelectSection,
                            saveRequestItem,
                            currentBin
                        }: NonSerialsEditProps) {
    const [qty, setQty] = useState<number | null>(null)
    const [selectedNonSerial, setSelectedNonSerial] =
        useState<CachedProduct | null>(null)
    const [editItem, setEditItem] = useState<SaveInventoryItem | null>(null)
    const [editQty, setEditQty] = useState<number | null>(null)
    const [products, setProducts] = useState<CachedProduct[]>([])
    const [savedQty, setSavedQty] = useState<number | null>(null)
    const [pendingQty, setPendingQty] = useState<number | null>(null)
    const [filterText, setFilterText] = useState('')
    const [filteredBinNonSerialItems, setFilteredBinNonSerialItems] = useState<
        SaveInventoryItem[] | []
    >([])
    const editQtyRef = useRef<number | null>(null)
    const [open, setOpen] = useState(false)
    const [selectedItem, setSelectedItem] = useState<SaveInventoryItem | null>(null)
    const [queuedEdits, setQueuedEdits] = useState<QueuedEdit[]>([])
    const audioPlayer = useRef<HTMLAudioElement | null>(null)
    const editFieldRef = useRef<HTMLDivElement>(null)
    const checkIconRef = useRef<HTMLButtonElement>(null)

    const playAudio = () => {
        if (audioPlayer.current) {
            audioPlayer.current.play().catch(error => {
                console.error('Error playing audio:', error)
            })
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                editFieldRef.current &&
                !editFieldRef.current.contains(event.target as Node) &&
                checkIconRef.current &&
                !checkIconRef.current.contains(event.target as Node)
            ) {
                cancelEdit()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [editItem])

    const cancelEdit = () => {
        setEditItem(null)
        setEditQty(null)
    }

    const handleClickOpen = (item: SaveInventoryItem) => {
        setSelectedItem(item)
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        setSelectedItem(null)
    }

    const handleCopy = (str: string) => {
        navigator.clipboard.writeText(str)
            .then(() => {
                alert('Copied to clipboard!')
            })
            .catch(err => {
                console.error('Failed to copy: ', err)
            })
    }

    const binNonSerialItems = useMemo(() => {
        if (!currentBin) return []

        let result: SaveInventoryItem[] = []
        let nonSerialQty = 0
        let nonSerialPendingQty = 0

        for (let item of nonSerialItems) {
            if (item.bin === currentBin) {
                result.push(item)
                let queuedEdit = queuedEdits.find(edit => edit.sku === item.sku)
                if (item.progress) {
                    if (queuedEdit) {
                        nonSerialPendingQty += Math.abs(queuedEdit.currentQty - queuedEdit.previousQty)
                        nonSerialQty += item.quantity
                    } else {
                        nonSerialPendingQty += item.quantity
                    }
                    if (item.progress === 'q-delete' || item.progress === 'deleting') {
                        nonSerialQty += item.quantity
                    }
                } else {
                    // if item is in queuedEdits, remove
                    if (queuedEdit) {
                        setQueuedEdits(prev => prev.filter(edit => edit.sku !== item.sku))
                    }
                    nonSerialQty += item.quantity
                }
            }
        }

        result.reverse()
        setSavedQty(nonSerialQty)
        setPendingQty(nonSerialPendingQty)
        setFilteredBinNonSerialItems(result)
        return result
    }, [nonSerialItems, currentBin])

    useEffect(() => {
        const fetchProducts = async () => {
            let cachedProducts = await getCachedProducts()
            setProducts(
                Object.values(cachedProducts).filter(item => !item.serialized)
            )
        }

        fetchProducts()
    }, [])

    const saveEditedItem = () => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }
        if (editItem && editQtyRef?.current !== null) {
            let currentQty = editQtyRef.current
            let item = binNonSerialItems.find(item => item.sku === editItem.sku)

            if (currentQty !== (item?.quantity || 0)) {
                let updatedItem = {...editItem, quantity: currentQty}
                saveRequestItem({
                    op: 'update',
                    sku: updatedItem.sku,
                    quantity: updatedItem.quantity,
                    bin: updatedItem.bin,
                    serial: null,
                    enteredAt: updatedItem.enteredAt,
                    excluded: updatedItem.excluded
                })

                // add the edited item and its quantities to the queue
                setQueuedEdits(prev => {
                    const existingEditIndex = prev.findIndex(edit => edit.sku === editItem.sku)
                    if (existingEditIndex !== -1) {
                        const updatedEdits = [...prev]
                        updatedEdits[existingEditIndex] = {
                            sku: editItem.sku,
                            previousQty: item?.quantity || 0,
                            currentQty: currentQty
                        }
                        return updatedEdits
                    } else {
                        return [
                            ...prev,
                            {
                                sku: editItem.sku,
                                previousQty: item?.quantity || 0,
                                currentQty: currentQty
                            }
                        ]
                    }
                })
            }

            setEditItem(null)
            setEditQty(null)
            editQtyRef.current = null
        }
    }

    const handleAddNonSerial = async () => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }
        let currentBinName = currentBin ? currentBin.startsWith('01') || isSpecialBin(currentBin) ? currentBin : `01 ${currentBin}` : null
        if (selectedNonSerial && qty && qty > 0) {
            saveRequestItem({
                op: 'add',
                sku: selectedNonSerial.sku,
                quantity: qty,
                bin: currentBinName,
                serial: null,
                enteredAt: new Date().toISOString()
            })

            setQty(null)
            setSelectedNonSerial(null)
            setFilterText('')
            setFilteredBinNonSerialItems(binNonSerialItems)
        }
    }

    const handleRemoveNonSerial = (sku: string) => {
        if (window.sessionStorage.getItem('blocking') === 'true') {
            playAudio()
            return
        }

        let itemToRemove = binNonSerialItems.find(item => item.sku === sku)

        if (itemToRemove) {
            saveRequestItem({
                op: 'delete',
                sku: itemToRemove.sku,
                quantity: itemToRemove.quantity,
                bin: itemToRemove.bin,
                serial: null,
                enteredAt: itemToRemove.enteredAt,
                excluded: itemToRemove.excluded
            })
        }

        setEditItem(null)
        setEditQty(null)
        editQtyRef.current = null
    }

    const handleQtyChange = (value: number) => {
        let sanitizedValue = Math.max(value, 0)
        setEditQty(sanitizedValue)
        editQtyRef.current = sanitizedValue
    }

    const handleIconClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        saveEditedItem()
    }

    const handleInputChange = (newValue: string | '') => {
        setFilterText(newValue)

        if (newValue) {
            let filteredItemSku = newValue.split(' - ')[0]
            let filterRegex = new RegExp(filteredItemSku, 'i')

            let filteredItems = binNonSerialItems.filter(item =>
                filterRegex.test(item.sku)
            )

            setFilteredBinNonSerialItems(filteredItems)
        } else {
            setFilteredBinNonSerialItems(binNonSerialItems)
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            if (window.sessionStorage.getItem('blocking') === 'true') {
                playAudio()
                return
            }
            event.preventDefault()
            handleAddNonSerial()
        }
    }

    const handleKeyDownEdit = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            if (window.sessionStorage.getItem('blocking') === 'true') {
                playAudio()
                return
            }
            event.preventDefault()
            saveEditedItem()
        }
    }

    return (
        <Box
            flex={1}
            p={2}
            border={2}
            borderColor={
                selectedSection === 'nonSerials' ? 'primary.main' : 'grey.400'
            }
            borderRadius={2}
            onClick={e => {
                e.stopPropagation()
                onSelectSection('nonSerials')
                window.sessionStorage.setItem('selectedSection', 'nonSerials')
            }}
            position="relative">
            {selectedSection !== 'nonSerials' && (
                <IconButton
                    size="small"
                    onClick={() => {
                        onSelectSection('nonSerials')
                        window.sessionStorage.setItem('selectedSection', 'nonSerials')
                    }}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8
                    }}>
                    <EditIcon />
                </IconButton>
            )}
            <audio ref={audioPlayer} src={excludedSound} />
            <div className="flex flex-row justify-between">
                <Typography variant="h5">Enter Non-Serials</Typography>
                {selectedSection === 'nonSerials' &&
                    binNonSerialItems.length > 0 && (
                        <Autocomplete
                            freeSolo
                            options={binNonSerialItems.map(
                                item =>
                                    `${item.sku} - ${
                                        products.find(
                                            product => item.sku === product.sku
                                        )?.model || ''
                                    }`
                            )}
                            value={filterText}
                            onInputChange={(_e, newValue) =>
                                handleInputChange(newValue)
                            }
                            onChange={(_e, newValue) => {
                                if (newValue) {
                                    let filteredItemSku =
                                        newValue.split(' - ')[0]
                                    let filteredItem = binNonSerialItems.find(
                                        item => item.sku === filteredItemSku
                                    )

                                    if (filteredItem) {
                                        setFilteredBinNonSerialItems([
                                            filteredItem
                                        ])
                                        setEditItem(filteredItem)
                                        setEditQty(filteredItem.quantity)
                                        editQtyRef.current =
                                            filteredItem.quantity
                                    }
                                }
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Filter saved items"
                                    variant="outlined"
                                    size="small"
                                    sx={{width: 200}}
                                />
                            )}
                        />
                    )}
            </div>
            <Typography variant="subtitle1" color="textSecondary">
                <span style={{fontWeight: 700, color: '#2e7d32'}}>Saved: </span>
                {savedQty === 1 ? `${savedQty} serial` : `${savedQty} serials`},{' '} <span
                style={{fontWeight: 700}}>Pending: </span>
                {pendingQty} quantity
            </Typography>
            {selectedSection === 'nonSerials' && (
                <div className="my-1 mx-auto grid max-w-5xl md:grid-cols-5 gap-y-4 md:gap-y-0">
                    <div className="grid gap-1 pr-4 sm:pt-4 md:pt-0 sm:w-full col-span-2">
                        <Autocomplete
                            disablePortal
                            id="sampleSelection"
                            options={products}
                            getOptionLabel={option =>
                                `${option.sku} - ${option.model || ''}`
                            }
                            value={selectedNonSerial}
                            onChange={(_e, newValue) =>
                                setSelectedNonSerial(newValue)
                            }
                            renderInput={params => (
                                <TextField
                                    required
                                    {...params}
                                    label="Select item"
                                />
                            )}
                        />
                    </div>
                    <div className="grid gap-1 pr-4 md:pt-0 col-span-2 md:mt-0 mt-4">
                        <TextField
                            required
                            type="number"
                            placeholder="Quantity"
                            value={qty ?? ''}
                            onChange={e => setQty(Number(e.target.value))}
                            onKeyDown={handleKeyDown}
                            className="flex"
                            label="Quantity"
                        />
                    </div>
                    <div className="grid gap-1 pr-4 sm:pt-4 md:pt-0 sm:w-full">
                        <Button
                            variant="contained"
                            className="text-right max-w-[100px]"
                            onClick={handleAddNonSerial}
                            disabled={window.sessionStorage.getItem('blocking') === 'true'}>
                            Add
                        </Button>
                    </div>
                </div>
            )}
            <TableContainer
                component={selectedSection === 'nonSerials' ? Paper : 'div'}
                className="my-3"
                style={{
                    maxHeight: 400,
                    overflow: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: 4
                }}>
                <Table size="small">
                    <TableHead>
                        <TableRow
                            style={{
                                borderBottom:
                                    filteredBinNonSerialItems.length > 0 &&
                                    filteredBinNonSerialItems[0].excluded
                                        ? 'none'
                                        : '2px solid #ddd'
                            }}>
                            <TableCell sx={{display: 'none'}}></TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell>Quantity</TableCell>
                            <TableCell>Status</TableCell>
                            {selectedSection === 'nonSerials' && (
                                <TableCell>Remove</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {binNonSerialItems.length > 0 ? (
                            filteredBinNonSerialItems.map(item => {
                                const excluded = item.progress === 'q-delete' || item.progress === 'deleting'
                                    ? '' : item.excluded
                                return (
                                    <TableRow
                                        key={item.sku}
                                        id={`row-${item.sku}`}
                                        sx={{
                                            border: item.excluded ? '2px solid red' : 'none',
                                            color: item.excluded ? 'red' : 'inherit'
                                        }}>
                                        <TableCell
                                            sx={{display: 'none'}}></TableCell>
                                        <TableCell>
                                            {item.sku}
                                            {' - '}
                                            {
                                                products.find(
                                                    product =>
                                                        item.sku === product.sku
                                                )?.model
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-row items-center">
                                                {editItem?.sku === item.sku ? (
                                                    <TextField
                                                        ref={editFieldRef}
                                                        type="number"
                                                        value={editQty ?? 1}
                                                        onChange={e => {
                                                            const newValue = e.target.value
                                                            handleQtyChange(newValue === '' ? 1 : Number(newValue))
                                                        }}
                                                        onClick={e => e.stopPropagation()}
                                                        onKeyDown={handleKeyDownEdit}
                                                        inputProps={{min: 1}}
                                                        style={{width: 60}}
                                                    />
                                                ) : (
                                                    <>
                                                        {item.quantity}
                                                        {selectedSection ===
                                                        'nonSerials' ? (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    setEditItem(item)
                                                                    setEditQty(item.quantity)
                                                                    editQtyRef.current = item.quantity
                                                                }}
                                                            >
                                                                <EditIcon
                                                                    style={{
                                                                        fontSize: 16
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </>
                                                )}
                                                {editItem?.sku === item.sku && (
                                                    <IconButton
                                                        ref={checkIconRef}
                                                        size="small"
                                                        onClick={handleIconClick}>
                                                        <SaveIcon
                                                            color="primary"
                                                        />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <StatusTooltip item={item} onClick={handleClickOpen} />
                                        </TableCell>
                                        {selectedSection === 'nonSerials' && (
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleRemoveNonSerial(
                                                            item.sku
                                                        )
                                                    }>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No saved non-serial items for {parseBinName(currentBin)}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {selectedItem && (
                <ExcludedDetailsDialog
                    open={open}
                    selectedItem={selectedItem}
                    onClose={handleClose}
                    onCopy={handleCopy}
                />
            )}
        </Box>
    )
}

export default NonSerialsEdit
